import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { api } from "store/slices/api";
import { useParams } from "react-router";
import Select from "react-select";
import { toast } from "react-toastify";
import isNil from "lodash/isNil";
import { calcuateLineItemTotal } from "../../utils";

const AddLaborCodeForm = ({ laborCodes, handleLaborCodeLookup }) => {
  const { quoteId, floorId } = useParams();
  const { storeCcn } = useSelector((state) => state.loggedInUser);
  const [laborCodeRow, setLaborCodeRow] = useState({});
  const quantityRef = useRef(null);
  const { data: quote = {} } = api.useGetPriceQuoteQuery(quoteId);
  const [getLaborPrice] = api.useLazyGetLaborPriceQuery();
  const [addLaborCode] = api.useAddFloorLaborCodeMutation();
  const { storeNumber, accountNumber, jobNumber, taxRate } = quote;
  const { price, laborCode, description, quantity } = laborCodeRow;
  const { data: allLaborCodes = [], isFetching: isLaborCodesFetching } = api.useGetLaborCodesQuery();

  const handleLaborCodeLookupValue = async ({ laborCodeDesc, laborCode }) => {
    setLaborCodeRow((prev) => ({ ...prev, laborCode, description: laborCodeDesc }));
    const payload = { accountNumber, jobNumber, storeNumber: storeNumber ?? storeCcn, laborCode };
    const result = await getLaborPrice(payload);
    if (result.error) {
      toast.error("Pricing failed for the selected labor code. Unit price defaulting to zero.");
      setLaborCodeRow((prev) => ({ ...prev, price: "0.00", oldPrice: "0.00" }));
    } else {
      const price = Math.round((result.data?.laborBillRate ?? 0.0) * 100) / 100;
      setLaborCodeRow((prev) => ({ ...prev, price, oldPrice: price }));
    }
  };

  const save = () => {
    const totals = calcuateLineItemTotal({ price, quantity, taxRate });
    const oldPrice = totals.price;
    const rank = laborCodes.length + 1;
    toast.promise(
      addLaborCode({ ...totals, oldPrice, floorId, laborCode, description, isTaxChecked: true, rank }).unwrap(),
      { success: "Labor added successfully", pending: "Adding labor item", error: "Something went wrong" },
    );
    handleLaborCodeLookup(false);
  };

  const disabled =
    price === "" || isNil(price) || isNil(laborCode) || ["", 0].includes(quantity) || !laborCodeRow.quantity;
  const lcs = allLaborCodes?.filter((lc) => !laborCodes.includes(lc.laborCode)) ?? [];

  return (
    <div className="d-flex flex-column mb-2 py-3 px-2 border rounded-1">
      <div className="search-form-container__fields">
        <form className="search-form-container__fields__form">
          <div className="d-flex justify-content-between flex-wrap">
            <div className="d-flex mb-1" style={{ flexGrow: 2 }}>
              <Form.Group className="me-3" style={{ flexGrow: 3 }}>
                <Form.Label>Labor Codes</Form.Label>
                <Select
                  name="laborCodes"
                  isLoading={isLaborCodesFetching}
                  loadingMessage={() => "Loading labor codes..."}
                  options={lcs.map((lc) => ({
                    ...lc,
                    value: lc.laborCode,
                    label: `${lc.laborCode} - ${lc.laborCodeDesc}`,
                  }))}
                  onChange={(lc) => handleLaborCodeLookupValue(lc)}
                  onMenuClose={() => quantityRef.current?.focus()}
                  autoFocus
                  defaultMenuIsOpen
                />
              </Form.Group>
              <Form.Group className="me-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  ref={quantityRef}
                  style={{ width: 150 }}
                  type="number"
                  placeholder="Enter Quantity"
                  value={quantity}
                  onChange={(e) =>
                    setLaborCodeRow({
                      ...laborCodeRow,
                      quantity: e.target.value === "" ? "" : Math.round(e.target.value * 100) / 100,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="me-3">
                <Form.Label>Unit Price</Form.Label>
                <Form.Control
                  style={{ width: 150 }}
                  type="number"
                  placeholder="Enter Price"
                  value={price}
                  onChange={(e) =>
                    setLaborCodeRow({
                      ...laborCodeRow,
                      price: e.target.value === "" ? "" : Math.round(Math.max(e.target.value, 0) * 100) / 100,
                    })
                  }
                />
              </Form.Group>
            </div>
            <div className="d-flex mb-1" style={{ flexGrow: 1 }}>
              <Button className="align-self-end" style={{ flexGrow: 1 }} onClick={save} disabled={disabled}>
                Add
              </Button>
              <Button
                variant="outline-primary"
                style={{ flexGrow: 1 }}
                className="align-self-end me-0"
                onClick={() => handleLaborCodeLookup(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLaborCodeForm;
